import { Table, type TableColumnDef } from '@sqs/rosetta-compositions';
import { Box, Flex, Text } from '@sqs/rosetta-primitives';
import React, { useEffect } from 'react';
import { PAGINATION_LIMIT_MIN } from '../../../const/pagination';
import { useGetSqspUserRoleMap } from '../../../hooks/useGetSqspUserRoleMap';
import { usePlatformBreakpoint } from '../../../hooks/usePlatformBreakpoint';
import { useI18n } from '../../../i18n';

import { PendingMigrationUser } from '../../../models/SqspUser';
import { selectEnterprise } from '../../../stores/currentUser';
import { fetchPendingMigrationUsers, selectAllPendingMigrationUsers } from '../../../stores/orgUsers';
import { RootState } from '../../../stores/rootReducer';
import { useAppDispatch, useAppSelector } from '../../../stores/store';
import { enterpriseHasSsoProvider } from '../../../utils/enterpriseUtils';
import { AcuityLoader } from '../../common/AcuityLoader';
import { TableHeader } from '../../common/Table/TableHeader';
import { TablePagination } from '../../common/Table/TablePagination';
import { PendingMigrationUsersListResultsTableData } from './PendingMigrationUsersListResultsTableData';
import { useOrgUserActions } from '../../../hooks/useOrgUserActions';

interface PendingMigrationUsersListResultsProps {
  readonly showSearch: boolean;
}

export const PendingMigrationUsersListResults = ({ showSearch }: PendingMigrationUsersListResultsProps) => {
  const { isMobile } = usePlatformBreakpoint();
  const { formatDateGeneric, t } = useI18n();
  const dispatch = useAppDispatch();
  const { getUserRole } = useGetSqspUserRoleMap();

  const { renderOrgActionModals } = useOrgUserActions();

  const { currentUserId, enterpriseId, hasSsoProvider, hasLoaded, users } = useAppSelector((state: RootState) => {
    const enterprise = selectEnterprise(state.currentUser);
    const hasLoadedUsers = state.pendingMigrationUsers && !state.pendingMigrationUsers.isLoading;

    return {
      currentUserId: state.currentUser?.currentUser.id,
      enterpriseId: enterprise?.id,
      hasSsoProvider: enterpriseHasSsoProvider(enterprise),
      hasLoaded: hasLoadedUsers,
      users: selectAllPendingMigrationUsers(state.pendingMigrationUsers)
    };
  });

  // TODO: This does not factor in fetching more than 1000 users
  //  https://squarespace.atlassian.net/browse/AE-754
  useEffect(() => {
    if (!enterpriseId) {
      throw new Error('User is not connected to an Enterprise');
    }

    dispatch(fetchPendingMigrationUsers(enterpriseId));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!currentUserId || !enterpriseId) {
    return null;
  }

  let dateAddedTooltip = t("Date when this user account was created in your organization.",

  {}, {
    project: 'enterprise-dashboard' });

  const dateAddedLabel = t("Date created", {}, { project: 'enterprise-dashboard' });

  if (hasSsoProvider) {
    dateAddedTooltip = t("The date a user first signed in.", {}, { project: 'enterprise-dashboard' });
  }

  const columns: TableColumnDef<PendingMigrationUser, any>[] = [
  {
    accessorKey: 'email',
    sortingFn: 'alphanumeric',
    header: ({ column }): any =>
    <TableHeader label={t("Email Address", null, { project: 'enterprise-dashboard' })} column={column} />,

    cell: (cell) =>
    <Text.Body m={0} sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {cell.getValue()}
        </Text.Body>

  },
  {
    accessorKey: 'role',
    header: ({ column }: any) =>
    <TableHeader
      label={t("Role", null, { project: 'enterprise-dashboard' })}
      column={column}
      tooltip={t("This is the user's role within the organization.", {}, { project: 'enterprise-dashboard' })} />,


    cell: (cell) => {
      return (
        <Text.Body m={0} fontSize={3}>
            {getUserRole(cell.row.original.role)}
          </Text.Body>);

    }
  },
  {
    accessorKey: 'dateAdded',
    header: ({ column }: any) => <TableHeader label={dateAddedLabel} column={column} tooltip={dateAddedTooltip} />,
    cell: (cell) => <Text.Body m={0}>{formatDateGeneric(cell.getValue())}</Text.Body>
  }];


  if (!hasLoaded && !users.length) {
    return (
      <Flex height="300px" justifyContent="center" alignItems="center" flexDirection="column">
        <AcuityLoader />
      </Flex>);

  }

  const shouldShowSearch = (!isMobile || showSearch) && users.length > 0;

  return (
    <Box position="relative">
      <Table
        columns={columns}
        data={users}
        enableSort
        enablePagination={users.length > 0}
        enableSearch={users.length > 0}
        initialState={{
          sorting: [
          {
            id: 'email',
            desc: false
          }],

          pagination: {
            pageIndex: 0,
            pageSize: PAGINATION_LIMIT_MIN
          }
        }}>

        <Table.Controls>
          <Table.Controls.Left>
            {isMobile &&
            <Table.Sort
              sortingOptions={[
              {
                id: 'email',
                ascLabel: t(" Email (A-Z)", {}, { project: 'enterprise-dashboard' }),
                descLabel: t(" Email (Z-A)", {}, { project: 'enterprise-dashboard' })
              },
              {
                id: 'role',
                ascLabel: t(" Role (ASC)", {}, { project: 'enterprise-dashboard' }),
                descLabel: t(" Role (DESC)", {}, { project: 'enterprise-dashboard' })
              },
              {
                id: 'dateAdded',
                ascLabel: t(" Date Created (ASC)", {}, { project: 'enterprise-dashboard' }),
                descLabel: t(" Date Created (DESC)", {}, { project: 'enterprise-dashboard' })
              }]
              } />

            }
            {shouldShowSearch &&
            <Table.Controls.Slot sx={{ marginBottom: isMobile ? 0 : 5 }}>
                <Table.Search isOpen={shouldShowSearch} />
                {!isMobile && <Table.RecordCount width="100%" />}
              </Table.Controls.Slot>
            }
          </Table.Controls.Left>
        </Table.Controls>
        <PendingMigrationUsersListResultsTableData enterpriseId={enterpriseId} hasLoaded={hasLoaded} users={users} />
        {users.length > 0 ? <TablePagination /> : null}
      </Table>
      {renderOrgActionModals()}
    </Box>);

};